import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/Button';
import { useAuth } from '../context/AuthContext';
import { useCustomerSelection } from '../context/CustomerSelectionContext';
import { partnerService } from '../services/partnerService';
import { User } from '../types/partner';
import { CustomerUserDetailModal } from '../components/users/CustomerUserDetailModal';
import { 
  Users, 
  UserPlus, 
  Shield, 
  Award, 
  Search, 
  X, 
  Check,
  Trash2,
  Mail,
  Pencil,
  ArrowUp,
  ArrowDown,
  ArrowUpDown
} from 'lucide-react';

type SortKey = 'name' | 'role' | 'status';
type SortDir = 'asc' | 'desc';

// Core pages this endpoint (50 by default). Pull every page so the list and the
// search cover the whole tenant rather than silently stopping at the first page.
const USERS_PAGE_SIZE = 200;
const MAX_USER_PAGES = 50;

// Role rank for sorting: super admin, then admin, then user.
function roleRank(u: User): number {
  if (u.isSuperAdmin) return 0;
  if (u.isAdmin || u.role === 'Admin') return 1;
  return 2;
}

// Module-level so the header button is not re-mounted (and does not lose focus)
// on every render of UsersView.
function SortHeader({ label, sort, sortKey, sortDir, onToggle }: {
  label: string;
  sort: SortKey;
  sortKey: SortKey;
  sortDir: SortDir;
  onToggle: (key: SortKey) => void;
}) {
  const active = sortKey === sort;
  const Icon = !active ? ArrowUpDown : sortDir === 'asc' ? ArrowUp : ArrowDown;
  return (
    <th
      className="text-left py-3 px-6 text-xs font-semibold text-slate-500 uppercase tracking-wider"
      aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        onClick={() => onToggle(sort)}
        className={`inline-flex items-center gap-1 uppercase tracking-wider hover:text-slate-800 transition-colors ${active ? 'text-slate-800' : ''}`}
        title={`Sort by ${label.toLowerCase()}`}
      >
        {label}
        <Icon className={`h-3 w-3 ${active ? '' : 'opacity-50'}`} />
      </button>
    </th>
  );
}

function compareUsers(a: User, b: User, key: SortKey): number {
  switch (key) {
    case 'role':
      return roleRank(a) - roleRank(b) || compareUsers(a, b, 'name');
    case 'status':
      return (a.status || '').localeCompare(b.status || '') || compareUsers(a, b, 'name');
    default:
      return `${a.firstName || ''} ${a.lastName || ''}`.trim().localeCompare(`${b.firstName || ''} ${b.lastName || ''}`.trim(), undefined, { sensitivity: 'base' })
        || (a.email || '').localeCompare(b.email || '');
  }
}

interface NewUserData {
  firstName: string;
  lastName: string;
  email: string;
  isAdmin: boolean;
  isSuperAdmin: boolean;
}

export function UsersView() {
  const [customerUsers, setCustomerUsers] = useState<{ [customerId: string]: User[] }>({});
  const [showAddUser, setShowAddUser] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [newUserData, setNewUserData] = useState<NewUserData>({ firstName: '', lastName: '', email: '', isAdmin: false, isSuperAdmin: false });
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadProgress, setLoadProgress] = useState<{ loaded: number; total: number } | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  
  const { partner } = useAuth();
  const { selectedCustomer: customer, isLoading: customerLoading } = useCustomerSelection();

  // Load users for a specific customer
  const loadCustomerUsers = useCallback(async (customerId: string) => {
    if (!partner?.partnerId) return;

    try {
      setIsLoading(true);
      setLoadProgress(null);
      const all: User[] = [];
      let page = 1;
      let totalPages = 1;
      do {
        const response = await partnerService.getPartnerUsers(partner.partnerId, {
          customerId,
          page,
          pageSize: USERS_PAGE_SIZE,
        });
        if (!response.success || !response.data) break;
        all.push(...response.data);
        totalPages = Math.max(1, response.totalPages || 1);
        setLoadProgress({ loaded: all.length, total: response.totalCount ?? all.length });
        page += 1;
      } while (page <= totalPages && page <= MAX_USER_PAGES);
      setCustomerUsers(prev => ({
        ...prev,
        [customerId]: all
      }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setIsLoading(false);
      setLoadProgress(null);
    }
  }, [partner?.partnerId]);

  // Load users for the selected customer
  useEffect(() => {
    if (customer && partner?.partnerId) {
      loadCustomerUsers(customer.customerId);
    }
  }, [customer, partner?.partnerId, loadCustomerUsers]);

  // Add new user to customer
  const addUserToCustomer = async () => {
    if (!partner?.partnerId || !customer || !newUserData.firstName || !newUserData.lastName || !newUserData.email) {
      setError('Please fill in all required fields');
      return;
    }

    try {
      setError(null);
      const response = await partnerService.addUserToCustomer(partner.partnerId, customer.customerId, newUserData);
      
      if (response.success && response.data) {
        await loadCustomerUsers(customer.customerId);
        setSuccessMessage('User added successfully');
        setShowAddUser(false);
        setNewUserData({ firstName: '', lastName: '', email: '', isAdmin: false, isSuperAdmin: false });
      } else {
        throw new Error(response.error || 'Failed to add user');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add user');
    }
  };

  // Apply a saved user (from the detail modal) to local state
  const onUserSaved = (saved: User) => {
    if (!customer) return;
    setCustomerUsers(prev => ({
      ...prev,
      [customer.customerId]: (prev[customer.customerId] || []).map(u =>
        u.userId === saved.userId ? { ...u, ...saved } : u
      )
    }));
    setSelectedUser(null);
    setSuccessMessage(`${saved.firstName} ${saved.lastName} updated`);
  };

  // Remove user from customer
  const removeUser = async (userId: string) => {
    if (!partner?.partnerId || !customer) return;
    
    if (!window.confirm('Are you sure you want to remove this user?')) return;

    try {
      setError(null);
      const response = await partnerService.removeUserFromCustomer(partner.partnerId, customer.customerId, userId);
      
      if (response.success) {
        await loadCustomerUsers(customer.customerId);
        setSuccessMessage('User removed successfully');
      } else {
        throw new Error(response.error || 'Failed to remove user');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove user');
    }
  };

  // Clear messages after a delay
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 8000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  if (customerLoading) {
    return (
      <div className="h-full flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-10 h-10 rounded-full border-2 border-slate-200 border-t-orange-600 animate-spin mx-auto mb-3" />
          <p className="text-sm text-slate-500">Loading users...</p>
        </div>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="h-full flex items-center justify-center bg-slate-50">
        <div className="text-center p-8">
          <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
            <Users className="h-8 w-8 text-slate-400" />
          </div>
          <h3 className="text-lg font-semibold text-slate-900 mb-2">No customer selected</h3>
          <p className="text-sm text-slate-500 max-w-sm">
            Please select a customer from the sidebar to view and manage their users.
          </p>
        </div>
      </div>
    );
  }

  const users = customerUsers[customer.customerId] || [];
  const activeUsers = users.filter(u => u.status === 'Active').length;
  const adminUsers = users.filter(u => u.isAdmin || u.role === 'Admin').length;
  
  const query = searchQuery.trim().toLowerCase();
  const filteredUsers = users.filter(u => 
    query === '' ||
    `${u.firstName} ${u.lastName}`.toLowerCase().includes(query) ||
    u.email.toLowerCase().includes(query)
  );
  const sortedUsers = [...filteredUsers].sort((a, b) => {
    const c = compareUsers(a, b, sortKey);
    return sortDir === 'asc' ? c : -c;
  });

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  return (
    <div className="h-full flex flex-col overflow-hidden bg-slate-50">
      {/* Header */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
              <Users className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-slate-900">User Management</h2>
              <p className="text-sm text-slate-500">{users.length} users • {customer.licenseCount} licenses</p>
            </div>
          </div>
          <Button size="sm" onClick={() => setShowAddUser(true)}>
            <UserPlus className="h-4 w-4 mr-2" />
            Add User
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {/* Messages */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700 flex items-center gap-2">
            <X className="h-4 w-4 flex-shrink-0" />
            {error}
          </div>
        )}

        {successMessage && (
          <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-sm text-emerald-700 flex items-center gap-2">
            <Check className="h-4 w-4 flex-shrink-0" />
            {successMessage}
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center">
                <Users className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-slate-900">{users.length}</div>
                <div className="text-xs text-slate-500">Total Users</div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center">
                <Check className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-slate-900">{activeUsers}</div>
                <div className="text-xs text-slate-500">Active</div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center">
                <Shield className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-slate-900">{adminUsers}</div>
                <div className="text-xs text-slate-500">Admins</div>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center">
                <Award className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <div className="text-2xl font-bold text-slate-900">{customer.licenseCount}</div>
                <div className="text-xs text-slate-500">Licenses</div>
              </div>
            </div>
          </div>
        </div>

        {/* Add User Modal */}
        {showAddUser && (
          <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
              <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
                <h3 className="font-semibold text-slate-900">Add New User</h3>
                <button onClick={() => setShowAddUser(false)} className="p-2 hover:bg-slate-100 rounded-lg transition-colors" title="Close" aria-label="Close dialog">
                  <X className="h-4 w-4 text-slate-500" />
                </button>
              </div>
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">First Name</label>
                    <input
                      type="text"
                      value={newUserData.firstName}
                      onChange={(e) => setNewUserData({ ...newUserData, firstName: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
                      placeholder="John"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">Last Name</label>
                    <input
                      type="text"
                      value={newUserData.lastName}
                      onChange={(e) => setNewUserData({ ...newUserData, lastName: e.target.value })}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
                      placeholder="Smith"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Email Address</label>
                  <input
                    type="email"
                    value={newUserData.email}
                    onChange={(e) => setNewUserData({ ...newUserData, email: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500"
                    placeholder="john.smith@company.com"
                  />
                </div>
                <label className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newUserData.isAdmin}
                    onChange={(e) => setNewUserData({ ...newUserData, isAdmin: e.target.checked })}
                    className="w-4 h-4 text-orange-600 border-slate-300 rounded focus:ring-orange-500"
                  />
                  <div>
                    <div className="text-sm font-medium text-slate-900">Administrator privileges</div>
                    <div className="text-xs text-slate-500">Can manage users and settings</div>
                  </div>
                </label>
                <label className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newUserData.isSuperAdmin}
                    onChange={(e) => setNewUserData({ ...newUserData, isSuperAdmin: e.target.checked, isAdmin: e.target.checked || newUserData.isAdmin })}
                    className="w-4 h-4 text-purple-600 border-slate-300 rounded focus:ring-purple-500"
                  />
                  <div>
                    <div className="text-sm font-medium text-slate-900">Super Admin privileges</div>
                    <div className="text-xs text-slate-500">Unrestricted access to every module; can manage other admins in Aitryx</div>
                  </div>
                </label>
              </div>
              <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-2 bg-slate-50 rounded-b-2xl">
                <Button variant="outline" onClick={() => setShowAddUser(false)}>Cancel</Button>
                <Button onClick={addUserToCustomer}>Add User</Button>
              </div>
            </div>
          </div>
        )}

        {selectedUser && partner?.partnerId && (
          <CustomerUserDetailModal
            partnerId={partner.partnerId}
            customerId={customer.customerId}
            user={selectedUser}
            onClose={() => setSelectedUser(null)}
            onSaved={onUserSaved}
          />
        )}

        {/* Users Table */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          {/* Table Header */}
          <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
            <h3 className="font-semibold text-slate-900">
              Users
              <span className="ml-2 text-xs font-normal text-slate-500">
                {query ? `${sortedUsers.length} of ${users.length}` : users.length}
              </span>
            </h3>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search users..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-orange-500/20 focus:border-orange-500 w-64"
              />
            </div>
          </div>

          {/* Table Content */}
          {isLoading ? (
            <div className="p-12 text-center">
              <div className="w-8 h-8 rounded-full border-2 border-slate-200 border-t-orange-600 animate-spin mx-auto mb-3" />
              <p className="text-sm text-slate-500">
                {loadProgress && loadProgress.total > loadProgress.loaded
                  ? `Loading users... ${loadProgress.loaded} of ${loadProgress.total}`
                  : 'Loading users...'}
              </p>
            </div>
          ) : sortedUsers.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <SortHeader label="User" sort="name" sortKey={sortKey} sortDir={sortDir} onToggle={toggleSort} />
                    <SortHeader label="Role" sort="role" sortKey={sortKey} sortDir={sortDir} onToggle={toggleSort} />
                    <SortHeader label="Status" sort="status" sortKey={sortKey} sortDir={sortDir} onToggle={toggleSort} />
                    <th className="text-right py-3 px-6 text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {sortedUsers.map((user) => (
                    <tr
                      key={user.userId}
                      className="hover:bg-slate-50/50 transition-colors cursor-pointer"
                      onClick={() => setSelectedUser(user)}
                      title="View role and module permissions"
                    >
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center text-slate-600 font-medium">
                            {user.firstName?.charAt(0)}{user.lastName?.charAt(0)}
                          </div>
                          <div>
                            <div className="font-medium text-slate-900">{user.firstName} {user.lastName}</div>
                            <div className="text-sm text-slate-500 flex items-center gap-1">
                              <Mail className="h-3 w-3" />
                              {user.email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-2">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                            user.isAdmin || user.role === 'Admin' 
                              ? 'bg-orange-50 text-orange-700 ring-1 ring-orange-600/20' 
                              : 'bg-slate-100 text-slate-600'
                          }`}>
                            {(user.isAdmin || user.role === 'Admin') && <Shield className="h-3 w-3" />}
                            {user.isAdmin ? 'Admin' : user.role || 'User'}
                          </span>
                          {user.isSuperAdmin && (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-purple-50 text-purple-700 ring-1 ring-purple-600/20">
                              <Award className="h-3 w-3" />
                              Super Admin
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                          user.status === 'Active' 
                            ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-600/20' 
                            : 'bg-red-50 text-red-700 ring-1 ring-red-600/20'
                        }`}>
                          {user.status}
                        </span>
                      </td>
                      <td className="py-4 px-6 text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => setSelectedUser(user)}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg border border-slate-200 text-slate-600 hover:text-orange-600 hover:bg-orange-50 transition-colors whitespace-nowrap"
                            title="Edit role and permissions"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                            Permissions
                          </button>
                          <button
                            onClick={() => removeUser(user.userId)}
                            className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Remove user"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-12 text-center">
              <div className="w-16 h-16 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
                <Users className="h-8 w-8 text-slate-400" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">
                {searchQuery ? 'No users found' : 'No users yet'}
              </h3>
              <p className="text-sm text-slate-500 mb-4">
                {searchQuery ? 'Try adjusting your search' : 'Add your first user to get started'}
              </p>
              {!searchQuery && (
                <Button onClick={() => setShowAddUser(true)}>
                  <UserPlus className="h-4 w-4 mr-2" />
                  Add First User
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

